import { autoUpdate, FloatingPortal, offset, useFloating } from '@floating-ui/react';
import { Button, Chip } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { TextBox, TextBoxChangeEvent, TextBoxHandle } from '@progress/kendo-react-inputs';
import React, { useRef, useState } from 'react';
import { InfoCircleOutline } from '../../../../../icons/icons';
import { MyFleetAirportModeOptions } from '../../../../../types/dropdown-options';
import { DropdownListWithValueField } from '../../../../components/inputs/DropdownListWithValueField';
import MarkerText from '../../../../components/inputs/MarkerText';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store-hooks';
import { setSetting } from '../../../../state/slices/settings-slice';
import { useSettingDropdownValue, useSettingStringValue } from '../../../home/hooks/settings';
import { useSearchContext } from '../../context/SettingsSearchContext';

const MY_FLEET_AIRLINE_MAX_COUNT = 5;

export function SettingsMyFleetAirlinesElement() {
    const [confirmationPopupOpen, setConfirmationPopupOpen] = useState(false);
    const textBoxRef = useRef<TextBoxHandle>(null);
    const airlines = useAppSelector((x) => x.settings.myFleetAirlines);
    const dispatch = useAppDispatch();
    const context = useSearchContext();
    const [input, setInput] = React.useState('');
    const { floatingStyles, refs } = useFloating({
        whileElementsMounted: autoUpdate,
        placement: 'bottom',
        elements: {
            reference: textBoxRef.current?.element
        },
        open: confirmationPopupOpen,
        middleware: [offset({ mainAxis: 5 })]
    });

    const saveAirline = (id: string) => {
        dispatch(setSetting('myFleetAirlines', [...airlines, id]));
    };

    const tryAddAirline = (id: string) => {
        if (airlines.length >= MY_FLEET_AIRLINE_MAX_COUNT) {
            return;
        }

        if (id.length === 2) {
            setInput(id);
            setConfirmationPopupOpen(true);
            return;
        } else if (id.length !== 3) {
            return;
        }

        if (!airlines.includes(id)) {
            saveAirline(id);
        }

        setInput('');
    };

    const updateInput = (e: TextBoxChangeEvent) => {
        setConfirmationPopupOpen(false);
        const includedComma = (e.value as string).includes(',');
        const val = (e.value as string)
            .trim()
            .toUpperCase()
            .replaceAll(/[^A-Za-z]/g, '')
            .slice(0, 3);
        if (includedComma) {
            tryAddAirline(val);
        } else {
            setInput(val);
        }
    };

    const handleReturnKeyPressed = () => {
        const airlineId = input.trim().replace(',', '').toUpperCase();
        if (confirmationPopupOpen) {
            setConfirmationPopupOpen(false);
            if (!airlines.includes(airlineId)) {
                saveAirline(airlineId);
            }
            setInput('');
        } else {
            tryAddAirline(airlineId);
        }
    };

    const removeAirline = (id: string) => {
        dispatch(
            setSetting(
                'myFleetAirlines',
                airlines.filter((x) => x !== id)
            )
        );
    };

    return (
        <div className="px-3" id="my-fleet-airline-list">
            <div className="d-flex align-items-center py-0 gap-2">
                <div className="flex-shrink-0">
                    <MarkerText highlight={context.term}>{`Airlines (max ${MY_FLEET_AIRLINE_MAX_COUNT})`}</MarkerText>
                </div>
                <div className="flex-grow-1 d-flex flex-wrap justify-content-end gap-2 py-2">
                    {airlines.map((x, i) => (
                        <Chip key={i} removable onRemove={() => removeAirline(x)}>
                            {x}
                        </Chip>
                    ))}
                </div>
                <div className="d-flex flex-column justify-content-center" style={{ minHeight: 55, width: 300 }}>
                    <TextBox
                        ref={textBoxRef}
                        onKeyDown={(e) => e.key === 'Enter' && handleReturnKeyPressed()}
                        placeholder="Enter airlines IDs and press , or enter"
                        className="input-uppercase"
                        value={input}
                        onChange={updateInput}
                    />
                </div>
                <FloatingPortal>
                    {confirmationPopupOpen && (
                        <div className="z-1" style={floatingStyles} ref={refs.setFloating}>
                            <div className="k-card p-2" style={{ width: 300 }}>
                                <div>
                                    <div className="d-flex align-items-center gap-2">
                                        <SvgIcon icon={InfoCircleOutline} themeColor="info" className="flex-shrink-0" size="large" />
                                        My fleet uses ICAO airline IDs. These are usually 3-character, and rarely 2-character. Please confirm
                                        you have entered a 2-character ICAO airline ID and not an IATA airline ID.
                                    </div>
                                    <div className="d-flex gap-2 pt-2 justify-content-between">
                                        <Button
                                            themeColor="primary"
                                            className="flex-grow-1"
                                            style={{ flexBasis: 0 }}
                                            onClick={() => {
                                                handleReturnKeyPressed();
                                            }}>
                                            Continue anyway
                                        </Button>
                                        <Button
                                            className="flex-grow-1"
                                            style={{ flexBasis: 0 }}
                                            fillMode="outline"
                                            onClick={() => setConfirmationPopupOpen(false)}>
                                            Close
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </FloatingPortal>
            </div>
        </div>
    );
}

export function SettingsMyFleetAirportElement() {
    const airportMode = useSettingDropdownValue(MyFleetAirportModeOptions, 'myFleetAirportMode');
    const airportName = useSettingStringValue('myFleetAirport', { uppercase: true, debounceMs: 1000 });
    const context = useSearchContext();

    return (
        <div className="px-3 d-flex align-items-center gap-2">
            <div className="flex-grow-1">
                <MarkerText highlight={context.term}>Airports</MarkerText>
            </div>

            <div style={{ width: 250 }}>
                <DropdownListWithValueField {...airportMode} />
            </div>
            <div>at</div>
            <div className="flex-shrink-1" style={{ width: 100 }}>
                <TextBox className="input-uppercase" {...airportName} />
            </div>
        </div>
    );
}

export function SettingsMyFleetDeskFilterElement() {
    const dispatch = useAppDispatch();
    const deskFilter = useAppSelector((x) => x.settings.myFleetDeskFilter);
    const context = useSearchContext();
    const [input, setInput] = React.useState('');

    const addFilter = (value: string) => {
        const val = value.toUpperCase().replaceAll(',', '').trim();
        if (!val) {
            return;
        }
        if (!deskFilter.includes(val)) {
            dispatch(setSetting('myFleetDeskFilter', [...deskFilter, val]));
        }
        setInput('');
    };

    const updateInput = (e: TextBoxChangeEvent) => {
        const val = e.value as string;
        setInput(val);
        if (val.includes(',')) {
            addFilter(val);
        }
    };

    const removeFilter = (filter: string) => {
        dispatch(
            setSetting(
                'myFleetDeskFilter',
                deskFilter.filter((x) => x !== filter)
            )
        );
    };

    return (
        <div className="px-3 d-flex align-items-center gap-2">
            <div>
                <MarkerText highlight={context.term}>Desk filter</MarkerText>
            </div>
            <div className="flex-grow-1 d-flex flex-wrap justify-content-end gap-2 py-2">
                {deskFilter.map((x, i) => (
                    <Chip key={i} removable onRemove={() => removeFilter(x)}>
                        {x}
                    </Chip>
                ))}
            </div>
            <div className="d-flex flex-column justify-content-center" style={{ minHeight: 55, width: 300 }}>
                <TextBox
                    onKeyDown={(e) => e.key === 'Enter' && addFilter(input)}
                    placeholder="Enter IDs and press , or enter"
                    className="input-uppercase"
                    value={input}
                    onChange={updateInput}
                />
            </div>
        </div>
    );
}
